import clsx from 'clsx'
import { Helmet } from 'react-helmet'
import kebabCase from 'lodash.kebabcase'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import { useHistory, match } from 'react-router-dom'
import { Drawer } from 'antd'

import {
  useCompanyDetails,
  useShowMessage,
  useUtils,
  useGlobalState,
  useMessage,
} from '@pepper/ui'

import Search from 'container/Projects/CreateProjectV2/components/search'
import MarketplaceBanner from 'components/Banners/MarketplaceBanner/MarketplaceBanner'
import TalentListBanner from 'components/Banners/TalentListBanner/TalentListBanner'
import ContentTypesBanner from 'components/Banners/ContentTypesBanner/ContentTypesBanner'

import CategorySideBar from './MarketplaceCategories/CategorySideBar'
import ScrollButton from './ScrollButton'
import DrawerHeader from './DrawerHeader'
import ServicesList from './MarketplaceCategories/ServicesList'

import useUserAccountType from 'utils/hooks/useUserAccountType'
import { useCategorySearch } from './hooks/useCategorySearch'
import { useDataVerticals } from './hooks/useDataVerticals'
import { useScroll } from './hooks/useScroll'

import { useSelectedCompanyId } from 'utils/functions/misc'

import {
  formatPreData,
  formatSubcategories,
  handleCreateProject,
  Mode,
  usePrefetchData,
} from './helper'

import styles from './_.module.css'

type DrawerProps = {
  title: string
  mode: Mode
  projectId?: string
  companyId?: string
  match: match<{ id: string }>
  projectBody?: Record<string, unknown>
}

const ServiceSelectionDrawer = NiceModal.create<DrawerProps>(
  ({
    mode = Mode.CREATE_PROJECT,
    title = 'Category Selection',
    projectId,
    projectBody,
  }) => {
    const modal = useModal()
    const history = useHistory()
    const { showError } = useMessage()
    const accountType = useUserAccountType()
    const companyId = useSelectedCompanyId()
    const { permissions } = useGlobalState()
    const { show: showCreateContentModal } = useModal('create-seo-content')
    const { sub_categories: rawSubCategories, verticals } = useUtils()
    const { data: company } = useCompanyDetails(
      `/companies/${companyId}`,
      companyId
    )

    const hasMarketplaceSubscription = permissions?.marketplace

    const [preDatasetRaw] = usePrefetchData(mode, title)
    const preDataset = formatPreData(preDatasetRaw, accountType, company)
    const subCategories = formatSubcategories(
      rawSubCategories,
      accountType,
      preDataset
    )

    const { searchedType, searchedVerticals, handleSearch } =
      useCategorySearch()

    const { recentVerticalOptions, popularVerticalOptions } = useDataVerticals({
      companyId,
      preDataset,
    })

    const { cardsLayoutScroll, showBottom } = useScroll()

    const onClose = () => {
      history.push({ hash: '' })
      modal.remove()
    }

    const handleClick = async (verticalId: number) => {
      if (!projectId) {
        showCreateContentModal({
          topics: [],
          contentType: { value: verticalId },
          shouldDirectToBrief: true,
        })
        modal.remove()
        return
      }
      const categoryId =
        verticals
          .find(item => item.id === verticalId)
          ?.category.id.toString() ?? ''

      const { status, data, error } = await handleCreateProject(
        companyId,
        categoryId,
        verticalId,
        projectId,
        projectBody
      )
      if (status === 'ok' && data !== null) {
        history.push({
          pathname: '/create-project-flow',
          search: `draft_id=${data.id}&projectId=${projectId}`,
        })
      } else {
        showError(error)
      }
      modal.remove()
    }

    return (
      <Drawer
        className={styles.drawer}
        title={<DrawerHeader />}
        placement="bottom"
        width={500}
        height="large"
        onClose={onClose}
        visible={modal.visible}
      >
        <Helmet>
          <title>Create Order</title>
        </Helmet>
        {preDataset && (
          <div
            className={clsx(styles.contentWrapper, {
              [styles.bannerWrapper]: !hasMarketplaceSubscription,
            })}
            data-id={
              mode === Mode.ADD_ASSIGNMENT
                ? 'verticalSelectionModal_addAssignment'
                : 'verticalSelectionModal_createProject'
            }
          >
            {hasMarketplaceSubscription ? (
              <>
                <CategorySideBar
                  subCategories={subCategories}
                  recentVerticals={recentVerticalOptions}
                />
                <div className={styles.cardsLayout} ref={cardsLayoutScroll}>
                  <div className={styles.searchBox}>
                    <ScrollButton top={kebabCase('Popular Orders')} />
                    <ScrollButton
                      bottom={kebabCase('Blogs')}
                      showBottom={showBottom}
                    />
                    <Search
                      placeholder={
                        preDataset?.typeContentSelection.body.search.placeHolder
                      }
                      searchOnChange={(val: string) =>
                        handleSearch(val, preDataset, mode)
                      }
                      value={searchedType ?? ''}
                    />
                  </div>
                  {!searchedType && (
                    <div>
                      {Array.isArray(popularVerticalOptions) &&
                        popularVerticalOptions.length > 0 && (
                          <ServicesList
                            handleClick={handleClick}
                            title="Popular Orders"
                            verticals={popularVerticalOptions}
                          />
                        )}
                      {Array.isArray(recentVerticalOptions) &&
                        popularVerticalOptions.length > 0 && (
                          <ServicesList
                            handleClick={handleClick}
                            title="Recent Orders"
                            verticals={recentVerticalOptions}
                          />
                        )}

                      {preDataset?.typeContentSelection.body.options.map(
                        subcategories => {
                          return (
                            <ServicesList
                              handleClick={handleClick}
                              key={subcategories.name}
                              title={subcategories.name ?? ''}
                              verticals={subcategories.options}
                            />
                          )
                        }
                      )}
                    </div>
                  )}
                  {searchedType && (
                    <ServicesList
                      handleClick={handleClick}
                      showTitle={true}
                      title={`Search Results: ${searchedVerticals.length} results found`}
                      verticals={searchedVerticals}
                    />
                  )}
                </div>
              </>
            ) : (
              <>
                <MarketplaceBanner />
                <TalentListBanner />
                <ContentTypesBanner />
              </>
            )}
          </div>
        )}
      </Drawer>
    )
  }
)

export default ServiceSelectionDrawer
